import { FC, lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import TopBarProgress from "react-topbar-progress-indicator";
import { getCSSVariableValue } from "../../_metronic/assets/ts/_utils";
import { WithChildren } from "../../_metronic/helpers";
import { MasterLayout } from "../../_metronic/layout/MasterLayout";
import { RequirePermission } from "../modules/auth";
import { useAuth } from "../modules/auth/core/Auth";
import CareerPage from "../pages/Career/CareerPage";
import { CareerCreatePage, CareerEditPage } from "../pages/Career/components";
import FacultyRegistrationDetails from "../pages/FacultyRegistration/FacultyRegistrationDetails";
import FacultyRegistrationForm from "../pages/FacultyRegistration/FacultyRegistrationForm";
import { MeasuredQualitiesEditPage } from "../pages/MeasuredQualities/components";
import MeasuredQualitiesCreatePage from "../pages/MeasuredQualities/components/MeasuredQualitiesCreatePage";
import { MeasuredQualityTypesEditPage } from "../pages/MeasuredQualityTypes/components";
import MeasuredQualityTypesCreatePage from "../pages/MeasuredQualityTypes/components/MeasuredQualityTypesCreatePage";
import QuestionSectionCreatePage from "../pages/QuestionSections/components/QuestionSectionCreatePage";
import QuestionSectionEditPage from "../pages/QuestionSections/components/QuestionSectionEditPage";
import QuestionSectionPage from "../pages/QuestionSections/CreateQuestionSection";
import { ToolEditPage } from "../pages/Tool/components";
import ToolCreatePage from "../pages/Tool/components/ToolCreatePage";
import UploadExcelFile from "../pages/UploadExcelFile/UploadExcelFile";
// Update these paths to the correct locations of your components
import Assessments from "../pages/CreateAssessment/Assessment";
// import AssessmentCreatePage from "../pages/CreateAssessment/components/assessment/AssessmentCreatePage";
import QuestionareCreateSinglePage from "../pages/CreateAssessment/components/questionaire/QuestionareCreateSinglePage";
import QuestionareEditSinglePage from "../pages/CreateAssessment/components/questionaire/QuestionareEditSinglePage";
import AssessmentEditPage from "../pages/CreateAssessment/components/assessment/AssessmentEditandCreatePage";
import AssessmentToolPage from "../pages/CreateAssessment/components/AssessmentToolPage";
import AssessmentUploadFile from "../pages/CreateAssessment/components/AssessmentUploadFile";
import AssessmentSection from "../pages/CreateAssessment/components/AssessmentSection";
import AssessmentQuestion from "../pages/CreateAssessment/components/AssessmentQuestion";
import ContactPersonCreatePage from "../pages/ContactPerson/components/ContactPersonCreatePage";
import ContactPersonPage from "../pages/ContactPerson/ContactPersonPage";
import LoginPage from "../pages/Login/components/LoginPage";
import Users from "../pages/Users/components/Users";
import UserRegistration from "../pages/Users/components/UserRegistration";
import ListCreatePage from "../pages/List/components/ListCreatePage";
import ListEditPage from "../pages/List/components/ListEditPage";
import ListPage from "../pages/List/CreateList";
import SchoolDashboardPage from "../pages/Dashboards/SchoolDashboardPage";
import SchoolNavigatorDashboardPage from "../pages/Dashboards/SchoolNavigatorDashboardPage";
import SchoolCombinedDashboardPage from "../pages/Dashboards/SchoolCombinedDashboardPage";
// import QuestionaireList from "../pages/CreateAssessment/components/questionaire/QuestionaireListPage";
import QuestionaireListPage from "../pages/CreateAssessment/components/questionaire/QuestionaireListPage";
import StudentsList from "../pages/StudentInformation/StudentsList";
import GroupCreatePage from "../pages/dashboard/widgets/CreateNewGroup";
import StudentCreatePage from "../pages/dashboard/widgets/CreateNewStudent";
import GroupStudentPage from "../pages/GroupStudent/GroupStudentPage";
import ReminderManagementPage from "../pages/ReminderManagement/ReminderManagementPage";
// import StudentManagementPage from "../pages/GroupStudent/StudentManagementPage";
import StudentListPage from "../pages/GroupStudent/StudentListPage";
// GroupStudentAdminPage removed — consolidated into Data Download (/group-student)
import GroupStudentSchoolPage from "../pages/GroupStudent/GroupStudentSchoolPage";
import AssignedStudentsPage from "../pages/GroupStudent/AssignedStudentsPage";
import ReportGenerationPage from "../pages/ReportGeneration/ReportGenerationPage";
import BetReportGenerationPage from "../pages/ReportGeneration/BetReportGenerationPage";
import NavigatorReportGenerationPage from "../pages/NavigatorReportGeneration/NavigatorReportGenerationPage";
import UnifiedReportManagementPage from "../pages/UnifiedReportManagement/UnifiedReportManagementPage";
import SendReportsPage from "../pages/SendReports/SendReportsPage";
import GamePage from "../pages/Games/GamePage";
import DemographicFieldsPage from "../pages/DemographicFields/DemographicFieldsPage";
import DemographicFieldCreatePage from "../pages/DemographicFields/components/DemographicFieldCreatePage";
import DemographicFieldEditPage from "../pages/DemographicFields/components/DemographicFieldEditPage";
// import QuestionareEditSinglePage from "../pages/CreateAssessment/components/questionaire/QuestionareEditSinglePage";
import CareerSuggestionPage from "../pages/CareerSuggestion/CareerSuggestionPage";
import DashboardAdminPage from "../pages/demo-dashboard-v2/dashboard-admin";
import InstituteDashboard from "../pages/dashboard/InstituteDashboard";
import ActivityLogPage from "../pages/ActivityLog/ActivityLogPage";
import JwtTokensPage from "../pages/JwtTokens/JwtTokensPage";
import LeadsPage from "../pages/Leads/LeadsPage";
import LiveTrackingPage from "../pages/LiveTracking/LiveTrackingPage";
import CommunicationLogsPage from "../pages/CommunicationLogs/CommunicationLogsPage";
import ReportsPage from "../pages/Reports/ReportsPage";
import ReportsHubPage from "../pages/ReportsHub/ReportsHubPage";
import AdminAssessmentEditPage from "../pages/ReportsHub/AdminAssessmentEdit/AdminAssessmentEditPage";
import StudentDashboard from "../pages/StudentDashboard/StudentDashboard";
import ClassTeacherDashboard from "../pages/ClassTeacherDashboard/ClassTeacherDashboard";
import StudentManagementPage from "../pages/GroupStudent/StudentManagementPage";
import StudentPortalLayout from "../pages/StudentDashboard/student-portal/StudentPortalLayout";
import { PROFILE_PATH, profileIncomplete } from "../pages/StudentDashboard/student-portal/studentProfileGate";


/** Backwards-compat: legacy /student-dashboard/:studentId -> /dashboard/student/view/:studentId */
const RedirectStudentDashboard: FC = () => {
  const { studentId } = useParams<{ studentId: string }>();
  return <Navigate to={`/dashboard/student/view/${studentId ?? ""}`} replace />;
};

// Paths that every logged-in user can access without a per-route permission check.
// Preserved verbatim per Phase 17 locked decision — Phase 17 does NOT add or remove entries.
// 17-04's monitoring guidance uses this list as "routes a logged-in-but-no-perms user can still
// reach" — DENY decisions on these in `auth_audit` are bugs, not legitimate access controls.
//
// Note: This constant is currently only documentation/monitoring scaffolding. The actual
// "always-allowed" behavior is enforced by the absence of <RequirePermission> wrappers on
// the corresponding <Route> elements below. See plan 17-02 for the per-route mapping.
const ALWAYS_ALLOWED = [
  "/dashboard",
  "/auth",
  "/login",
  "/student-login",
  "/studentAssessment",
  "/allotted-assessment",
  "/general-instructions",
  "/demographics",
  "/b2c",
];
// Suppress "declared but never read" — kept intentionally per locked decision (see comment above).
void ALWAYS_ALLOWED;

const AuthorizedLayout = () => {
  // Per-route permission checks now happen via <RequirePermission> wrappers on
  // each <Route element=…>. This layout no longer enforces URL-pattern auth —
  // it just renders the MasterLayout shell. The ALWAYS_ALLOWED list above is kept
  // for future use if we ever need to short-circuit BOTH permission AND login for
  // a path, but Phase 17 does not invoke it from this component (the routes
  // inside ALWAYS_ALLOWED simply have no <RequirePermission> wrapper — see route
  // definitions below).
  return <MasterLayout />;
};

// Canonical student-role predicate. Tolerates ROLE_-prefixed Spring authorities.
const STUDENT_ROLE_NAMES = ["STUDENT", "B2C_STUDENT", "ROLE_STUDENT", "ROLE_B2C_STUDENT"];
const isStudentRoleName = (r: unknown): boolean =>
  typeof r === "string" && STUDENT_ROLE_NAMES.includes(r);

/**
 * Role-based guard for the standalone student portal (decoupled from the admin
 * permission/URL-whitelist system on purpose). A route is allowed iff the session
 * user is a super-admin (full bypass, for inspection) OR carries a student role.
 * Everyone else is bounced to the unified login. This replaces the per-page
 * <RequirePermission> on the portal so a student never lands on the admin
 * RequestAccessPage inside their own shell.
 */
const StudentAuthGuard: FC = () => {
  const { currentUser } = useAuth();
  if (!currentUser) return <Navigate to="/auth/login" replace />;
  if (currentUser.superAdmin) return <Outlet />;
  const roles = currentUser.roles || [];
  return roles.some(isStudentRoleName) ? <Outlet /> : <Navigate to="/auth/login" replace />;
};

/**
 * Super-admin-only route guard (JWT token console). No permission code exists
 * for this surface on purpose — the backend enforces `principal.superAdmin`
 * directly, so the route gate mirrors that rather than inventing a grantable
 * code that the server would ignore.
 */
const RequireSuperAdmin: FC<WithChildren> = ({ children }) => {
  const { currentUser } = useAuth();
  return currentUser?.superAdmin === true ? <>{children}</> : <Navigate to="/dashboard" replace />;
};

/**
 * Post-login gate: a student whose one-time profile step isn't done is routed to
 * /student/dashboard/student-info before any dashboard page. `infoCompleted` comes
 * from /auth/me (MeResponse). Non-students and super-admins pass through, and the
 * info form itself is never gated (or it would loop). The page asked for rides
 * along as `state.from`, so saving the form returns the student to it.
 */
const StudentInfoGate: FC<WithChildren> = ({ children }) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  if (profileIncomplete(currentUser) && location.pathname !== PROFILE_PATH) {
    return (
      <Navigate to={PROFILE_PATH} replace state={{ from: location.pathname + location.search }} />
    );
  }
  return <>{children}</>;
};

const PrivateRoutes = () => {
  // ── Full seal for pure students ──────────────────────────────────────────────
  // A "pure student" (carries a student role, no staff role, not super-admin) lives
  // ONLY under /student/*. Any other path — /dashboard, the "/" → /dashboard index,
  // or any admin route — bounces to their dashboard, so they can never reach the
  // admin MasterLayout / aside. Super-admins and any staff/dual-role user are NOT
  // sealed (they keep full admin access and can still inspect the student portal).
  // /logout, /auth/* and /student/* are matched before this (logout in AppRoutes) or
  // are inside the student space, so sign-out and the portal itself are unaffected.
  const { currentUser } = useAuth();
  const { pathname } = useLocation();
  const roles = currentUser?.roles || [];
  const isPureStudent =
    !currentUser?.superAdmin &&
    roles.length > 0 &&
    roles.some(isStudentRoleName) &&
    roles.every(isStudentRoleName);
  if (isPureStudent && !pathname.startsWith("/student/")) {
    return <Navigate to="/student/dashboard" replace />;
  }

  const StudentsData = lazy(
    () => import("../pages/UniversityResult/StudentData")
  );
  const ResultPage = lazy(
    () => import("../pages/UniversityResult/FinalResult")
  );
  const StudentRegistrationDetails = lazy(
    () => import("../pages/StudentRegistration/StudentRegistrationDetails")
  );
  const ForgotPassword = lazy(
    () => import("../pages/Forgot-Password/ForgotPassword")
  );

  const GoogleGroups = lazy(
    () => import("../pages/GoogleGroups/GroupMembers/GoogleGroups")
  );

  const Groups = lazy(() => import("../pages/dashboard/widgets/Groups"));

  const Group = lazy(() => import("../pages/Group/Group"));

  const OldStudentEmail = lazy(
    () => import("../pages/OldStudentEmail/OldStudentEmail")
  );

  const StudentRegistrationForm = lazy(
    () => import("../pages/StudentRegistration/StudentRegistrationForm")
  );

  const CourseBranchBatchPageForm = lazy(
    () => import("../pages/StudentRegistration/CourseBranchBatchPage")
  );


  const StudentList = lazy(
    () => import("../pages/StudentInformation/StudentsList")
  );
  const StudentProfile = lazy(
    () => import("../pages/StudentInformation/StudentProfile")
  );

  // R2: separate student login dissolved — students sign in via the unified /auth
  // login page (student/DOB mode). Kept commented per the no-deletion policy.
  // const StudentDashboardLogin = lazy(
  //   () => import("../pages/StudentDashboard/student-portal/StudentDashboardLogin")
  // );
  const StudentPortalDashboard = lazy(
    () => import("../pages/StudentDashboard/student-portal/StudentPortalDashboard")
  );
  const StudentImpersonationLanding = lazy(
    () => import("../pages/StudentDashboard/student-portal/StudentImpersonationLanding")
  );
  const InsightDashboard = lazy(
    () => import("../pages/StudentDashboard/insight/InsightDashboard")
  );
  const StudentInfoForm = lazy(
    () => import("../pages/StudentDashboard/student-portal/StudentInfoForm")
  );
  const StudentPortalNavigator360 = lazy(
    () => import("../pages/StudentDashboard/student-portal/StudentNavigator360Page")
  );
  const StudentPortalAssessments = lazy(
    () => import("../pages/StudentDashboard/student-portal/StudentAssessments")
  );
  const StudentPortalReports = lazy(
    () => import("../pages/StudentDashboard/student-portal/StudentReports")
  );
  const StudentPaymentReturn = lazy(
    () => import("../pages/StudentDashboard/student-portal/StudentPaymentReturn")
  );
  const StudentCounsellingPage = lazy(
    () => import("../pages/Counselling/student/StudentCounsellingPage")
  );
  const SlotBookingPage = lazy(
    () => import("../pages/Counselling/student/SlotBookingPage")
  );

  // const Compiler = lazy(() => import("../pages/Compiler/compiler"));

  const MeasuredQualityTypes = lazy(
    () => import("../pages/MeasuredQualityTypes/CreateMeasuredQualityTypes")
  );
  const MeasuredQualities = lazy(
    () => import("../pages/MeasuredQualities/MeasuredQualities")
  );
  const Tools = lazy(() => import("../pages/Tool/CreateTool"));
  const College = lazy(() => import("../pages/College/CollegePage"));
  // Standalone twin of the institute list's "Groups" action — same manager panel.
  const GroupManagementPage = lazy(
    () => import("../pages/GroupManagement/GroupManagementPage")
  );
  const CohortInsightsPage = lazy(() => import("../pages/SchoolAdmin/CohortInsightsPage"));
  // Named apart from the module-scope SchoolDashboardPage import (the per-institute
  // "Dashboard 1" popup) — a local const of that name would shadow it in here.
  const SchoolInsightsDashboardPage = lazy(
    () => import("../pages/SchoolDashboard/SchoolDashboardPage")
  );

  // The admin side of the same feature: generate, withdraw, trace and announce a
  // school's dashboard. Lazy for the same reason the dashboard is — neither is on the
  // path most users take through the app.
  const SchoolDashboardReleasePage = lazy(
    () => import("../pages/SchoolDashboard/SchoolDashboardReleasePage")
  );
  // The Navigator Pro college dashboard: same release store, its own page.
  const NavigatorProDashboardPage = lazy(
    () => import("../pages/SchoolDashboard/navigatorPro/NavigatorProDashboardPage")
  );
  // Update the import path below to the correct location if the file exists elsewhere
  const CollegeCreatePage = lazy(() => import("../pages/College/CollegePage"));
  const AssessmentMappingPage = lazy(() => import("../pages/AssessmentMapping/AssessmentMappingPage"));
  const AssessmentQuestions = lazy(
    () => import("../pages/AssesmentQuestions/CreateQuestion")
  );
  const QuestionCreatePage = lazy(
    () => import("../pages/AssesmentQuestions/components/QuestionCreatePage")
  );
  const QuestionEditPage = lazy(
    () => import("../pages/AssesmentQuestions/components/QuestionEditPage")
  );
  const QuestionDuplicatesPage = lazy(
    () => import("../pages/AssesmentQuestions/components/QuestionDuplicatesPage")
  );
  const OfflineAssessmentUpload = lazy(
    () => import("../pages/OfflineAssessmentUpload/OfflineAssessmentUploadPage")
  );
  const OMRDataUpload = lazy(
    () => import("../pages/OfflineAssessmentUpload/OMRDataUploadPage")
  );
  const TextResponseMapping = lazy(
    () => import("../pages/TextResponseMapping/TextResponseMappingPage")
  );
  const OldDataMappingPage = lazy(
    () => import("../pages/OldDataMapping/OldDataMappingPage")
  );
  const ScoreDebugPage = lazy(
    () => import("../pages/ScoreDebug/ScoreDebugPage")
  );
  const Board = lazy(() => import("../pages/Board/BoardPage"));
  const Section = lazy(() => import("../pages/Section/SectionPage"));
  const Course = lazy(() => import("../pages/Course/CoursePage"));
  const Branch = lazy(() => import("../pages/Branch/BranchPage"));
  const Batch = lazy(() => import("../pages/Batch/BatchPage"));
  const Session = lazy(() => import("../pages/Session/SessionPage"));
  const BatchGoogle = lazy(() => import("../pages/BatchGoogle/BatchGoogle"));
  const Role = lazy(() => import("../modules/role/Role"));
  const RoleRoleGroupPage = lazy(
    () => import("../modules/role_roleGroup/Role_RoleGroup")
  );
  const RoleUser = lazy(() => import("../modules/roleUser/RoleUser1"));
  const RolesAndPermissionsPage = lazy(() => import("../pages/RolesAndPermissions/RolesAndPermissionsPage"));
  const UserManagementPage = lazy(() => import("../pages/UserManagement/UserManagementPage"));
  const UniversityResultDashboard = lazy(
    () => import("../pages/UniversityResult/UniversityResultDashboard")
  );
  const CounsellorDashboardPage = lazy(() => import("../pages/Counselling/counsellor/CounsellorDashboardPage"));
  const AvailabilityManagerPage = lazy(() => import("../pages/Counselling/counsellor/AvailabilityManagerPage"));
  const SessionNotesPage = lazy(() => import("../pages/Counselling/counsellor/SessionNotesPage"));
  const CounsellingDashboardPage = lazy(() => import("../pages/Counselling/admin/CounsellingDashboardPage"));
  const CounsellorManagementPage = lazy(() => import("../pages/Counselling/admin/CounsellorManagementPage"));
  const SlotManagementPage = lazy(() => import("../pages/Counselling/admin/SlotManagementPage"));
  const ManageStudentsPage = lazy(() => import("../pages/Counselling/admin/ManageStudentsPage"));
  const StudentMappingAdminPage = lazy(() => import("../pages/Counselling/admin/StudentMappingAdminPage"));
  const CounsellingNotificationsPage = lazy(() => import("../pages/Counselling/admin/CounsellingNotificationsPage"));
  const CounsellingSessionsPage = lazy(() => import("../pages/Counselling/admin/CounsellingSessionsPage"));
  const ReportTemplatesPage = lazy(() => import("../pages/ReportTemplates/ReportTemplatesPage"));
  const WebsiteRegistrationPage = lazy(() => import("../pages/WebsiteRegistration/WebsiteRegistrationPage"));
  const ReportCenterPage = lazy(() => import("../pages/ReportCenter/ReportCenterPage"));
  const PaymentTrackingPage = lazy(() => import("../pages/PaymentTracking/PaymentTrackingPage"));
  const PromoCodePage = lazy(() => import("../pages/PromoCode/PromoCodePage"));
  const ReferralCodePage = lazy(() => import("../pages/ReferralCode/ReferralCodePage"));
  const PaymentRegisterPage = lazy(() => import("../pages/PaymentTracking/PaymentRegisterPage"));
  const B2CPricingTierPage = lazy(() => import("../pages/B2C/PricingTier/PricingTierPage"));
  const B2CCampaignPage = lazy(() => import("../pages/B2C/Campaign/CampaignPage"));
  const B2CCampaignEditPage = lazy(() => import("../pages/B2C/Campaign/CampaignEditPage"));
  const B2CTrackerPage = lazy(() => import("../pages/B2C/Tracker/TrackerPage"));
  const EmailAccountsPage = lazy(() => import("../pages/EmailAccounts/EmailAccountsPage"));
  const McpTokensPage = lazy(() => import("../pages/McpTokens/McpTokensPage"));
  const EmailLogPage = lazy(() => import("../pages/EmailLog/EmailLogPage"));
  const WhatsAppLogPage = lazy(() => import("../pages/WhatsAppLog/WhatsAppLogPage"));
  const EmailTemplatesPage = lazy(() => import("../pages/EmailTemplates/EmailTemplatesPage"));
  const EmailRecipientsPage = lazy(() => import("../pages/EmailRecipients/EmailRecipientsPage"));
  // const UniversityAllResultDashboard = lazy(
  //   () => import("../pages/UniversityResult/UniversityAllResultDashboard")
  // );
  return (
    <Routes>

      {/* The old mock reset flow (enter-email → check-email → change-password) is
          retired; the real flow is /auth/forgot-password → emailed link →
          /auth/reset-password/:token. Old links land on the real entry point. */}
      <Route
        path="/login/reset-password/*"
        element={<Navigate to="/auth/forgot-password" replace />}
      />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />


      <Route path="/login" element={<LoginPage />} />

      {/* Standalone "view student dashboard" — opened by the admin Data Download
          "Dashboard" button. Renders the student portal page WITHOUT the Metronic
          aside menu, so the admin sees what the student sees when they log in.
          Permission-protected but layout-free. */}
      <Route path="/student/dashboard-preview" element={
        <RequirePermission perm="student.read">
          <SuspensedView>
            <StudentPortalDashboard />
          </SuspensedView>
        </RequirePermission>
      } />

      {/* Admin impersonation landing — opened in a new tab by the Data Download
          "Open as Student" button. Establishes a cookie-less student session
          from ?t=<jwt> and redirects to /student/dashboard. Layout-free; no
          RequirePermission (the minted student JWT is the authorization). */}
      <Route path="/student/impersonate" element={
        <SuspensedView>
          <StudentImpersonationLanding />
        </SuspensedView>
      } />

      {/* Real per-student assessment Insight Dashboard (data-driven, per engine).
          Opened by the admin group-student "Dashboard" button for students who have
          a generated report. Layout-free (no Metronic aside) so the new tab shows
          only the dashboard. Reads ?studentId=&assessmentId=&name= */}
      <Route path="/student/insight-dashboard" element={
        <RequirePermission perm="generated_report.read">
          <SuspensedView>
            <InsightDashboard />
          </SuspensedView>
        </RequirePermission>
      } />

      {/* Standalone school dashboards — opened from the institutes list "Dashboard 1 /
          Dashboard 2" actions via window.open(..., "_blank"). Rendered WITHOUT the
          Metronic aside menu so only the dashboard surface shows in the new tab. */}
      <Route path="/dashboard/school/:id" element={
        <RequirePermission perm="dashboard.principal.read">
          <SuspensedView>
            <SchoolDashboardPage />
          </SuspensedView>
        </RequirePermission>
      } />
      <Route path="/dashboard/school-navigator/:id" element={
        <RequirePermission perm="dashboard.principal.read">
          <SuspensedView>
            <SchoolNavigatorDashboardPage />
          </SuspensedView>
        </RequirePermission>
      } />
      <Route path="/dashboard/school-combined/:id" element={
        <RequirePermission perm="dashboard.principal.read">
          <SuspensedView>
            <SchoolCombinedDashboardPage />
          </SuspensedView>
        </RequirePermission>
      } />

      {/* ─────────────────────────────────────────────────────────────────────
          Student portal — dedicated lightweight shell (own aside menu), fully
          decoupled from the admin MasterLayout. A student who signs in via the
          unified /auth page (student mode) lands here and never loads admin chrome.
          URLs stay at /student/dashboard/* (unchanged from before). The admin-only
          "view a specific student" / insight-preview tools keep their own routes
          (/student/dashboard/view/:studentId, /student/insight-dashboard,
          /student/dashboard-preview) above, inside the admin shell.
          ───────────────────────────────────────────────────────────────────── */}
      {/* Role-based guard (StudentAuthGuard) → dedicated shell (StudentPortalLayout) →
          pages. No <RequirePermission> here: the portal is decoupled from admin ABAC,
          so a student is gated purely by their role + the one-time info-completion
          gate, never by the admin URL whitelist / RequestAccessPage. */}
      <Route element={<StudentAuthGuard />}>
        <Route element={<StudentPortalLayout />}>
          {/* Profile form — always reachable when logged in (it's the info-gate target,
              so it must NOT be wrapped in StudentInfoGate or it would redirect-loop). */}
          <Route path="/student/dashboard/student-info" element={
            <SuspensedView>
              <StudentInfoForm />
            </SuspensedView>
          } />
          <Route path="/student/dashboard" element={
            <StudentInfoGate>
              <SuspensedView>
                <StudentPortalDashboard />
              </SuspensedView>
            </StudentInfoGate>
          } />
          <Route path="/student/dashboard/navigator-360" element={
            <StudentInfoGate>
              <SuspensedView>
                <StudentPortalNavigator360 />
              </SuspensedView>
            </StudentInfoGate>
          } />
          <Route path="/student/dashboard/assessments" element={
            <StudentInfoGate>
              <SuspensedView>
                <StudentPortalAssessments />
              </SuspensedView>
            </StudentInfoGate>
          } />
          <Route path="/student/dashboard/reports" element={
            <StudentInfoGate>
              <SuspensedView>
                <StudentPortalReports />
              </SuspensedView>
            </StudentInfoGate>
          } />
          {/* The student's own data-driven Insight Dashboard (self mode). */}
          <Route path="/student/dashboard/insight" element={
            <StudentInfoGate>
              <SuspensedView>
                <InsightDashboard self />
              </SuspensedView>
            </StudentInfoGate>
          } />
          <Route path="/student/dashboard/counselling" element={
            <StudentInfoGate>
              <SuspensedView>
                <StudentCounsellingPage />
              </SuspensedView>
            </StudentInfoGate>
          } />
          <Route path="/student/dashboard/counselling/book" element={
            <StudentInfoGate>
              <SuspensedView>
                <SlotBookingPage />
              </SuspensedView>
            </StudentInfoGate>
          } />
          {/* Post-Razorpay return for a dashboard purchase. Outside StudentInfoGate so a
              returning payer always reaches the confirmation (and onward into the unlocked
              dashboard). Fixes a previously unmounted route — StudentPortalDashboard builds
              this exact returnUrl for checkout. */}
          <Route path="/student/payment-return" element={
            <SuspensedView>
              <StudentPaymentReturn />
            </SuspensedView>
          } />
        </Route>
      </Route>

      {/* payment-status and payment-register moved to public AppRoutes */}
      <Route element={<AuthorizedLayout />}>
        <Route path="auth/*" element={<Navigate to="/dashboard" />} />
        <Route path="dashboard" element={<DashboardAdminPage />} />

        <Route path="/school/principal/dashboard/:id" element={
          <RequirePermission perm="dashboard.principal.read">
            <SuspensedView>
              <InstituteDashboard />
            </SuspensedView>
          </RequirePermission>
        } />
        <Route path="/reminders" element={
          <RequirePermission perm="reminders.view">
            <SuspensedView>
              <ReminderManagementPage />
            </SuspensedView>
          </RequirePermission>
        } />
        <Route
          path="/student/university/result-list"
          element={
            <RequirePermission perm="university_mark.read">
              <SuspensedView>
                <StudentsData />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route path="/game-list" element={
          <RequirePermission perm="game_table.read">
            <SuspensedView>
              <GamePage />
            </SuspensedView>
          </RequirePermission>
        } />
        <Route path="/questionaire/List" element={
          <RequirePermission perm="questionnaire.read">
            <SuspensedView>
              <QuestionaireListPage />
            </SuspensedView>
          </RequirePermission>
        } />
        <Route path="/school/principal/dashboard/studentList" element={
          <RequirePermission perm="student.read">
            <SuspensedView>
              <StudentsList />
            </SuspensedView>
          </RequirePermission>
        } />
        <Route path="/students/${member.id}/dashboard" element={
          <RequirePermission perm="student.read">
            <SuspensedView>
              <StudentsList />
            </SuspensedView>
          </RequirePermission>
        } />
        <Route path="/school/groups" element={
          <RequirePermission perm="group.read">
            <SuspensedView>
              <Groups />
            </SuspensedView>
          </RequirePermission>
        } />
        <Route path="/school/group/create" element={
          <RequirePermission perm="group.create">
            <SuspensedView>
              <GroupCreatePage />
            </SuspensedView>
          </RequirePermission>
        } />
        <Route path="/school/student/create" element={
          <RequirePermission perm="student.create">
            <SuspensedView>
              <StudentCreatePage />
            </SuspensedView>
          </RequirePermission>
        } />

        <Route path="/group-student" element={
          <RequirePermission perm="student_group.read">
            <SuspensedView>
              <GroupStudentPage />
            </SuspensedView>
          </RequirePermission>
        } />

        {/* /student-management: clone of /group-student with every data-export
            path stripped (single answer + bulk answers + proctoring + report
            generation + report email). Keeps Student List xlsx, allot, reset,
            edit basic info, demographics view. Gated by its own permission
            so admins can hand out management without granting downloads. */}
        <Route path="/student-management" element={
          <RequirePermission perm="student_management.read">
            <SuspensedView>
              <StudentManagementPage />
            </SuspensedView>
          </RequirePermission>
        } />

        {/* /student-list: view-only sibling of /group-student. Same data, no
            download buttons, multi-select assessment picker per row. */}
        <Route path="/student-list" element={
          <RequirePermission perm="student.read">
            <SuspensedView>
              <StudentManagementPage/>
            </SuspensedView>
          </RequirePermission>
        } />

        <Route path="/school/group-student" element={
          <RequirePermission perm="student.read">
            <SuspensedView>
              <GroupStudentSchoolPage />
            </SuspensedView>
          </RequirePermission>
        } />

        <Route path="/school/assigned-students" element={
          <RequirePermission perm="student.read">
            <SuspensedView>
              <AssignedStudentsPage />
            </SuspensedView>
          </RequirePermission>
        } />

        <Route path="/report-generation" element={
          <RequirePermission perm="report_generation.read">
            <SuspensedView>
              <ReportGenerationPage />
            </SuspensedView>
          </RequirePermission>
        } />

        <Route path="/career-suggestion" element={
          <RequirePermission perm="career_suggestion.read">
            <SuspensedView>
              {/* <CareerSuggestionPage /> */}
            </SuspensedView>
          </RequirePermission>
        } />

        {/* Admin viewing a specific student's dashboard.
            New canonical path: /student/dashboard/view/:studentId
            Legacy /student-dashboard/:studentId kept as redirect below for old links. */}
        <Route path="/student/dashboard/view/:studentId" element={
          <RequirePermission perm="student.read">
            <SuspensedView>
              <StudentDashboard />
            </SuspensedView>
          </RequirePermission>
        } />
        <Route
          path="/student-dashboard/:studentId"
          element={<RedirectStudentDashboard />}
        />

        {/* Student portal pages moved OUT of the admin MasterLayout shell into the
            dedicated, lightweight <StudentPortalLayout /> group below (its own aside
            menu, no admin chrome). The /student/dashboard/* URLs are unchanged. */}

        {/* Backwards-compat redirects: legacy /student/* and /dashboard/student/* paths -> new /student/dashboard/* */}
        <Route path="/student/student-info"       element={<Navigate to="/student/dashboard/student-info" replace />} />
        <Route path="/student/navigator-360"      element={<Navigate to="/student/dashboard/navigator-360" replace />} />
        <Route path="/student/assessments"        element={<Navigate to="/student/dashboard/assessments" replace />} />
        <Route path="/student/reports"            element={<Navigate to="/student/dashboard/reports" replace />} />
        <Route path="/student/counselling"        element={<Navigate to="/student/dashboard/counselling" replace />} />
        <Route path="/student/counselling/book"   element={<Navigate to="/student/dashboard/counselling/book" replace />} />
        <Route path="/dashboard/student"                   element={<Navigate to="/student/dashboard" replace />} />
        <Route path="/dashboard/student/student-info"      element={<Navigate to="/student/dashboard/student-info" replace />} />
        <Route path="/dashboard/student/navigator-360"     element={<Navigate to="/student/dashboard/navigator-360" replace />} />
        <Route path="/dashboard/student/assessments"       element={<Navigate to="/student/dashboard/assessments" replace />} />
        <Route path="/dashboard/student/reports"           element={<Navigate to="/student/dashboard/reports" replace />} />
        <Route path="/dashboard/student/counselling"       element={<Navigate to="/student/dashboard/counselling" replace />} />
        <Route path="/dashboard/student/counselling/book"  element={<Navigate to="/student/dashboard/counselling/book" replace />} />
        <Route path="/dashboard/student/view/:studentId"   element={<RedirectStudentDashboard />} />

        <Route
          path="/student/university/result-dashboard"
          element={
            <RequirePermission perm="university_mark.read">
              <SuspensedView>
                <UniversityResultDashboard />
              </SuspensedView>
            </RequirePermission>
          }
        />
        {/*
                <Route
                  path="/student/university/all-result-dashboard"
                  element={
                    <SuspensedView>
                      <UniversityAllResultDashboard />
                    </SuspensedView>
                  }
                />
        */}
        <Route
          path="/student/university/result"
          element={
            <RequirePermission perm="university_mark.read">
              <SuspensedView>
                <ResultPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        {/* <Route
          path="pdf"
          element={
            <SuspensedView>
              <Pdf />
            </SuspensedView>
          }
        /> */}
        <Route
          path="/student/registrar/page"
          element={
            <RequirePermission perm="student.create">
              <SuspensedView>
                <CourseBranchBatchPageForm />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/student/registration-details"
          element={
            <RequirePermission perm="student.read">
              <SuspensedView>
                <StudentRegistrationDetails />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/faculty/registration-details"
          element={
            <RequirePermission perm="faculty.read">
              <SuspensedView>
                <FacultyRegistrationDetails />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/faculty/registration-form"
          element={
            <RequirePermission perm="faculty.create">
              <SuspensedView>
                <FacultyRegistrationForm />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/teacher/class-dashboard"
          element={
            <RequirePermission perm="dashboard.teacher.read">
              <SuspensedView>
                <ClassTeacherDashboard />
              </SuspensedView>
            </RequirePermission>
          }
        />

        <Route
          path="/registrar/verification/faculty"
          element={
            <RequirePermission perm="faculty.update">
              <SuspensedView>
                <FacultyRegistrationForm />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/registrar/verification"
          element={
            <RequirePermission perm="student.update">
              <SuspensedView>
                <StudentRegistrationForm />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/forgotpassword"
          element={
            <SuspensedView>
              <ForgotPassword />
            </SuspensedView>
          }
        />
        <Route
          path="google-groups"
          element={
            <RequirePermission perm="google_groups.read">
              <SuspensedView>
                <GoogleGroups />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="groups"
          element={
            <RequirePermission perm="group.read">
              <SuspensedView>
                <Groups />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="group"
          element={
            <RequirePermission perm="group.read">
              <SuspensedView>
                <Group />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/old-student-email"
          element={
            <RequirePermission perm="user.read">
              <SuspensedView>
                <OldStudentEmail />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/career"
          element={
            <RequirePermission perm="career.read">
              <SuspensedView>
                <CareerPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/career/create"
          element={
            <RequirePermission perm="career.create">
              <SuspensedView>
                <CareerCreatePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/career/edit/:id"
          element={
            <RequirePermission perm="career.update">
              <SuspensedView>
                <CareerEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/contact-person"
          element={
            <RequirePermission perm="contact_person.read">
              <SuspensedView>
                <ContactPersonPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/contact-person/create"
          element={
            <RequirePermission perm="contact_person.create">
              <SuspensedView>
                <ContactPersonCreatePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/board"
          element={
            <RequirePermission perm="institute.read">
              <SuspensedView>
                <Board />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/section"
          element={
            <RequirePermission perm="section.read">
              <SuspensedView>
                <Section />
              </SuspensedView>
            </RequirePermission>
          }
        />
        {
          <Route
            path="/college"
            element={
              <RequirePermission perm="institute.read">
                <SuspensedView>
                  <College />
                </SuspensedView>
              </RequirePermission>
            }
          />
        }
        <Route
          path="/college/create"
          element={
            <RequirePermission perm="institute.write">
              <SuspensedView>
                <CollegeCreatePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        {/* Group Management — the institute list's Groups modal as its own page.
            Read is the entry gate; the create / update / delete / member.manage /
            contact.assign permissions gate the individual controls inside. */}
        <Route
          path="/group-management"
          element={
            <RequirePermission perm="student_group.read">
              <SuspensedView>
                <GroupManagementPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/assessment-mapping"
          element={
            <RequirePermission perm="assessment_institute_mapping.read">
              <SuspensedView>
                <AssessmentMappingPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/question-sections"
          element={
            <RequirePermission perm="question_section.read">
              <SuspensedView>
                <QuestionSectionPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/question-sections/create"
          element={
            <RequirePermission perm="question_section.create">
              <SuspensedView>
                <QuestionSectionCreatePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/question-sections/edit/:id"
          element={
            <RequirePermission perm="question_section.update">
              <SuspensedView>
                <QuestionSectionEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        {/* <Route
          path="/college/edit/:id"
          element={
            <SuspensedView>
              <CollegeEditPage  />
            </SuspensedView>
          }
        /> */}
        <Route
          path="/assessment-questions"
          element={
            <RequirePermission perm="assessment_question.read">
              <SuspensedView>
                <AssessmentQuestions />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/assessment-questions/create"
          element={
            <RequirePermission perm="assessment_question.create">
              <SuspensedView>
                <QuestionCreatePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/assessment-questions/edit/:id"
          element={
            <RequirePermission perm="assessment_question.update">
              <SuspensedView>
                <QuestionEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/assessment-questions/duplicates"
          element={
            <RequirePermission perm="assessment_question.read">
              <SuspensedView>
                <QuestionDuplicatesPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/offline-assessment-upload"
          element={
            <RequirePermission perm="assessment_answer.create">
              <SuspensedView>
                <OfflineAssessmentUpload />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/omr-data-upload"
          element={
            <RequirePermission perm="omr_column_mapping.read">
              <SuspensedView>
                <OMRDataUpload />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/text-response-mapping"
          element={
            <RequirePermission perm="assessment_answer.update">
              <SuspensedView>
                <TextResponseMapping />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/tools"
          element={
            <RequirePermission perm="tool.read">
              <SuspensedView>
                <Tools />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/tools/create"
          element={
            <RequirePermission perm="tool.create">
              <SuspensedView>
                <ToolCreatePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/tools/edit/:id"
          element={
            <RequirePermission perm="tool.update">
              <SuspensedView>
                <ToolEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/list"
          element={
            <RequirePermission perm="list.read">
              <SuspensedView>
                <ListPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/list/create"
          element={
            <RequirePermission perm="list.read">
              <SuspensedView>
                <ListCreatePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/list/edit/:id"
          element={
            <RequirePermission perm="list.read">
              <SuspensedView>
                <ListEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/upload-excel"
          element={
            <RequirePermission perm="student.import_bulk">
              <SuspensedView>
                <UploadExcelFile />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/demographic-fields"
          element={
            <RequirePermission perm="demographic_field.read">
              <SuspensedView>
                <DemographicFieldsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/demographic-fields/create"
          element={
            <RequirePermission perm="demographic_field.create">
              <SuspensedView>
                <DemographicFieldCreatePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/demographic-fields/edit/:id"
          element={
            <RequirePermission perm="demographic_field.update">
              <SuspensedView>
                <DemographicFieldEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/assessments"
          element={
            <RequirePermission perm="assessment.read">
              <SuspensedView>
                <Assessments />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/assessments/create/step-2"
          element={
            <RequirePermission perm="assessment.create">
              <SuspensedView>
                <AssessmentToolPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/assessments/create/step-3"
          element={
            <RequirePermission perm="assessment.create">
              <SuspensedView>
                <AssessmentUploadFile />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/assessments/create/step-4"
          element={
            <RequirePermission perm="assessment.create">
              <SuspensedView>
                <AssessmentSection />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/assessments/create/step-5"
          element={
            <RequirePermission perm="assessment.create">
              <SuspensedView>
                <AssessmentQuestion />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/questionare/create"
          element={
            <RequirePermission perm="questionnaire.create">
              <SuspensedView>
                <QuestionareCreateSinglePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/questionare/edit/:id"
          element={
            <RequirePermission perm="questionnaire.update">
              <SuspensedView>
                <QuestionareEditSinglePage />
              </SuspensedView>
            </RequirePermission>
          }
        />

        <Route
          path="/assessments/create"
          element={
            <RequirePermission perm="assessment.create">
              <SuspensedView>
                <AssessmentEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/assessments/edit/:id"
          element={
            <RequirePermission perm="assessment.update">
              <SuspensedView>
                <AssessmentEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/measured-qualities"
          element={
            <RequirePermission perm="measured_quality.read">
              <SuspensedView>
                <MeasuredQualities />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/measured-qualities/create"
          element={
            <RequirePermission perm="measured_quality.create">
              <SuspensedView>
                <MeasuredQualitiesCreatePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/measured-qualities/edit/:id"
          element={
            <RequirePermission perm="measured_quality.update">
              <SuspensedView>
                <MeasuredQualitiesEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/measured-quality-types"
          element={
            <RequirePermission perm="measured_quality_type.read">
              <SuspensedView>
                <MeasuredQualityTypes />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/measured-quality-types/create"
          element={
            <RequirePermission perm="measured_quality_type.create">
              <SuspensedView>
                <MeasuredQualityTypesCreatePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/measured-quality-types/edit/:id"
          element={
            <RequirePermission perm="measured_quality_type.update">
              <SuspensedView>
                <MeasuredQualityTypesEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/course"
          element={
            <RequirePermission perm="institute_course.read">
              <SuspensedView>
                <Course />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/branch"
          element={
            <RequirePermission perm="institute_branch.read">
              <SuspensedView>
                <Branch />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/batch"
          element={
            <RequirePermission perm="institute_batch.read">
              <SuspensedView>
                <Batch />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/session"
          element={
            <RequirePermission perm="institute_session.read">
              <SuspensedView>
                <Session />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/batchgoogle"
          element={
            <RequirePermission perm="google_groups.update">
              <SuspensedView>
                <BatchGoogle />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/studentlist"
          element={
            <RequirePermission perm="student.read">
              <SuspensedView>
                <StudentList />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/studentprofile"
          element={
            <RequirePermission perm="student.read">
              <SuspensedView>
                <StudentProfile />
              </SuspensedView>
            </RequirePermission>
          }
        />
        {/* New consolidated pages */}
        <Route
          path="/user-management/roles/manage"
          element={
            <RequirePermission perm="role.read">
              <SuspensedView>
                <RolesAndPermissionsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/user-management/users/manage"
          element={
            <RequirePermission perm="user.read.all">
              <SuspensedView>
                <UserManagementPage />
              </SuspensedView>
            </RequirePermission>
          }
        />

        {/* Old routes — redirect to new pages (destination routes are permission-guarded; do
            NOT wrap a redirect in RequirePermission as it would flash RequestAccessPage). */}
        <Route path="/roles/role" element={<Navigate to="/user-management/roles/manage" replace />} />
        <Route path="/roles/role_roleGroup" element={<Navigate to="/user-management/roles/manage" replace />} />
        <Route path="/roles/roleUser" element={<Navigate to="/user-management/users/manage" replace />} />
        <Route path="/user-registrations" element={<Navigate to="/user-management/users/manage" replace />} />
        <Route path="/roles/users" element={<Navigate to="/user-management/users/manage" replace />} />
        <Route
          path="/reports"
          element={
            <RequirePermission perm="report.read">
              <SuspensedView>
                <ReportsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/bet-report-generation"
          element={
            <RequirePermission perm="bet_report_data.read">
              <SuspensedView>
                <BetReportGenerationPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/navigator-report-generation"
          element={
            <RequirePermission perm="navigator_report_data.read">
              <SuspensedView>
                <NavigatorReportGenerationPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/unified-report-management"
          element={
            <RequirePermission perm="report_generation.read">
              <SuspensedView>
                <UnifiedReportManagementPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/send-reports"
          element={
            <RequirePermission perm="report.export">
              <SuspensedView>
                <SendReportsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/reports-hub"
          element={
            <RequirePermission perm="report.read">
              <SuspensedView>
                <ReportsHubPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        {/* School-facing report delivery: preview/download/email reports.
            Own resource code so it is allottable per page — no mixing. */}
        <Route
          path="/report-center"
          element={
            <RequirePermission perm="report_center.read">
              <SuspensedView>
                <ReportCenterPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/school-admin/cohort-insights"
          element={
            <RequirePermission perm="dashboard.school.insights.read">
              <SuspensedView>
                <CohortInsightsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/school-dashboard"
          element={
            <RequirePermission perm="dashboard.school.read">
              <SuspensedView>
                <SchoolInsightsDashboardPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        {/* Its own code and its own path, not a sub-path of the School Dashboard: role_url
            is derived from permissions, so sharing dashboard.school.read put both menu
            items in front of every holder. A school principal and a college principal
            are different audiences and are granted separately. */}
        <Route
          path="/college-dashboard/navigator-pro"
          element={
            <RequirePermission perm="dashboard.college.read">
              <SuspensedView>
                <NavigatorProDashboardPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        {/* The old address, still in already-sent release emails. */}
        <Route
          path="/school-dashboard/navigator-pro"
          element={<Navigate to="/college-dashboard/navigator-pro" replace />}
        />
        {/* Releasing is an administrative act — it spends money and changes what a
            school can see — so it is gated on release, not on read. */}
        <Route
          path="/school-dashboard/releases"
          element={
            <RequirePermission perm="dashboard.school.release">
              <SuspensedView>
                <SchoolDashboardReleasePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin-assessment-edit/:assessmentId/:studentId"
          element={
            <RequirePermission perm="assessment_answer.update">
              <SuspensedView>
                <AdminAssessmentEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/activity-log"
          element={
            <RequirePermission perm="user_activity_log.read">
              <SuspensedView>
                <ActivityLogPage />
              </SuspensedView>
            </RequirePermission>
          }
        />

        <Route
          path="/admin/jwt-tokens"
          element={
            <RequireSuperAdmin>
              <SuspensedView>
                <JwtTokensPage />
              </SuspensedView>
            </RequireSuperAdmin>
          }
        />

        <Route
          path="/b2c/website-registration"
          element={
            <RequireSuperAdmin>
              <SuspensedView>
                <WebsiteRegistrationPage />
              </SuspensedView>
            </RequireSuperAdmin>
          }
        />

        <Route
          path="/live-tracking"
          element={
            <RequirePermission perm="live_tracking.read">
              <SuspensedView>
                <LiveTrackingPage />
              </SuspensedView>
            </RequirePermission>
          }
        />

        <Route
          path="/communication-logs"
          element={
            <RequirePermission perm="communication_log.read">
              <SuspensedView>
                <CommunicationLogsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />

        <Route
          path="/leads"
          element={
            <RequirePermission perm="lead.read">
              <SuspensedView>
                <LeadsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />

        <Route
          path="/old-data-mapping"
          element={
            <RequirePermission perm="firebase_data_mapping.read">
              <SuspensedView>
                <OldDataMappingPage />
              </SuspensedView>
            </RequirePermission>
          }
        />

        <Route
          path="/score-debug"
          element={
            <RequirePermission perm="intermediary_scores.read">
              <SuspensedView>
                <ScoreDebugPage />
              </SuspensedView>
            </RequirePermission>
          }
        />

        <Route
          path="/counsellor/dashboard"
          element={
            <RequirePermission perm="counselling.appointment.read">
              <SuspensedView>
                <CounsellorDashboardPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/counsellor/availability"
          element={
            <RequirePermission perm="counselling.availability_template.update">
              <SuspensedView>
                <AvailabilityManagerPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/counsellor/session-notes/:id"
          element={
            <RequirePermission perm="counselling.session_notes.update">
              <SuspensedView>
                <SessionNotesPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/counselling-dashboard"
          element={
            <RequirePermission perm="counselling.appointment.read">
              <SuspensedView>
                <CounsellingDashboardPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/counselling-sessions"
          element={
            <RequirePermission perm="counselling.appointment.read">
              <SuspensedView>
                <CounsellingSessionsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/counsellors"
          element={
            <RequirePermission perm="counsellor.read">
              <SuspensedView>
                <CounsellorManagementPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/counselling-students"
          element={
            <RequirePermission perm="counselling.student_management.read">
              <SuspensedView>
                <ManageStudentsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/counselling-mappings"
          element={
            <RequirePermission perm="counselling.student_counsellor_mapping.read">
              <SuspensedView>
                <StudentMappingAdminPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/counselling-slots"
          element={
            <RequirePermission perm="counselling.slot.read">
              <SuspensedView>
                <SlotManagementPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/counselling-notifications"
          element={
            <RequirePermission perm="counselling.notification.read">
              <SuspensedView>
                <CounsellingNotificationsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/report-templates"
          element={
            <RequirePermission perm="report_template.read">
              <SuspensedView>
                <ReportTemplatesPage />
              </SuspensedView>
            </RequirePermission>
          }
        />

        <Route
          path="/admin/email-accounts"
          element={
            <RequirePermission perm="email_account.read">
              <SuspensedView>
                <EmailAccountsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/mcp-tokens"
          element={
            <RequirePermission perm="mcp_token.read">
              <SuspensedView>
                <McpTokensPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/email-log"
          element={
            <RequirePermission perm="email_log.read">
              <SuspensedView>
                <EmailLogPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/whatsapp-log"
          element={
            <RequirePermission perm="whatsapp_log.read">
              <SuspensedView>
                <WhatsAppLogPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/email-templates"
          element={
            <RequirePermission perm="email_template.read">
              <SuspensedView>
                <EmailTemplatesPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/admin/email-recipients"
          element={
            <RequirePermission perm="email_recipient.read">
              <SuspensedView>
                <EmailRecipientsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />

        <Route
          path="/payment-tracking"
          element={
            <RequirePermission perm="payment.read">
              <SuspensedView>
                <PaymentTrackingPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/promo-codes"
          element={
            <RequirePermission perm="promo_code.read">
              <SuspensedView>
                <PromoCodePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/referral-codes"
          element={
            <RequirePermission perm="referral_code.read">
              <SuspensedView>
                <ReferralCodePage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/b2c/pricing-tiers"
          element={
            <RequirePermission perm="pricing_tier.read">
              <SuspensedView>
                <B2CPricingTierPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/b2c/campaigns"
          element={
            <RequirePermission perm="campaign.read">
              <SuspensedView>
                <B2CCampaignPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/b2c/campaigns/create"
          element={
            <RequirePermission perm="campaign.create">
              <SuspensedView>
                <B2CCampaignEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/b2c/campaigns/edit/:id"
          element={
            <RequirePermission perm="campaign.update">
              <SuspensedView>
                <B2CCampaignEditPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path="/b2c/tracker"
          element={
            <RequirePermission perm="tracker.read">
              <SuspensedView>
                <B2CTrackerPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        {/* Page Not Found */}
        {/* <Route path="*" element={<Navigate to="/error/404" />} /> */}
      </Route>
    </Routes>
  );
};

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--kt-primary");
  TopBarProgress.config({
    barColors: {
      "0": baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes };
